import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Home,
  Check,
  ChevronDown,
  LayoutGrid,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Moon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Logo } from "./Logo";
import { UserProfile } from "./UserProfile";
import { CollapsedUserAvatar } from "./CollapsedUserAvatar";
import { SidebarNavItems, NAV_ITEM_INACTIVE_CLASS } from "./SidebarNavItems";
import { getActiveSidebarItemId, pathMatches } from "./sidebarNavigation";

import { getAuthorizedHomeNavItems, HOME_NAV } from "@/pages/home/homeNav";
import { LIVE_NAV, useLiveNav } from "@/pages/live/liveNav";
import { ADMIN_NAV, useAdminNav } from "@/pages/admin/adminNav";
import { REPORTS_NAV } from "@/pages/measurement/reportsNav";
import { DASHBOARDS_NAV, useDashboardsNav } from "@/pages/workspace/dashboardsNav";
import { usePlacementNav } from "@/lib/dashboard/placementNav";

import { useNavSidebarState } from "@/hooks/useNavSidebarState";
import { useTheme } from "@/hooks/useTheme";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { useAccountSetup } from "@/hooks/useAccountSetup";

import type { SidebarItem } from "@/types/navigation";
import { SECTION_DEFINITIONS, type SectionId } from "@/config/uiConfig";

export const NAV_SIDEBAR_W_COLLAPSED = 60;
export const NAV_SIDEBAR_W_EXPANDED = 230;

const NAV_ITEM_SIZE_CLASS = "h-9 w-9";
const NAV_ITEM_HEIGHT_CLASS = "h-9";
const NAV_ITEM_RADIUS_CLASS = "rounded-md";
const NAV_ICON_CLASS = "h-[18px] w-[18px]";
const NAV_SECTION_X_PADDING_CLASS = "px-3";

export interface NavSidebarGroup {
  id: "home" | SectionId;
  label: string;
  href: string;
  basePath: string;
  icon: typeof Home;
  items: SidebarItem[];

  /**
   * Item highlighted when pathname is exactly the group base path.
   * Example: /analytics should highlight /analytics/.
   */
  defaultHref?: string;
}

export function getNavGroupEntryHref(group: NavSidebarGroup) {
  return group.defaultHref ?? group.href;
}

const SECTION_NAV_ITEMS: Record<SectionId, SidebarItem[]> = {
  live: LIVE_NAV,
  operations: [],
  investigations: [],
  journal: [],
  measurement: REPORTS_NAV,
  workspace: DASHBOARDS_NAV,
  dashboards: [],
  admin: ADMIN_NAV,
};

export const NAV_GROUPS: NavSidebarGroup[] = [
  {
    id: "home",
    label: "Home",
    href: "/home",
    basePath: "/home",
    icon: Home,
    items: HOME_NAV,
  },
  ...SECTION_DEFINITIONS.map((section): NavSidebarGroup => ({
    id: section.id,
    label: section.label,
    href: section.href,
    basePath: section.basePath,
    icon: section.icon,
    items: SECTION_NAV_ITEMS[section.id],
    defaultHref:
      section.id === "live"
        ? "/live/event-overview"
        : section.id === "workspace"
          ? "/workspace/dashboards"
          : section.id === "admin"
            ? "/admin/tenants"
            : undefined,
  })),
];

export function getAuthorizedNavGroups(
  groups: NavSidebarGroup[],
  canAccessSection: (sectionId: SectionId) => boolean,
) {
  return groups.filter((group) => group.id === "home" || canAccessSection(group.id));
}

export function useAuthorizedNavGroups() {
  const { canAccessSection } = useAuthorization();
  const { needsSetup } = useAccountSetup();

  return useMemo(() => {
    const permitted = getAuthorizedNavGroups(NAV_GROUPS, canAccessSection).filter(
      (group) => !needsSetup || group.id === "home",
    );
    const visibleIds = new Set<string>(permitted.map((group) => group.id));
    return permitted.map((group) =>
      group.id === "home"
        ? {
            ...group,
            items: group.items
              .filter((item) => item.id === "overview" || visibleIds.has(item.id))
              .map((item) => {
                const destination = permitted.find((entry) => entry.id === item.id);
                return destination ? { ...item, href: getNavGroupEntryHref(destination) } : item;
              }),
          }
        : group,
    );
  }, [canAccessSection, needsSetup]);
}

export function getActiveNavGroup(pathname: string, groups = NAV_GROUPS) {
  if (pathname === "/journal" || pathname.startsWith("/journal/"))
    return groups.find((group) => group.id === "live") ?? groups[0];
  return groups.find((group) => pathMatches(pathname, group.basePath)) ?? groups[0];
}

/**
 * Returns the nav items for a given group.
 * Dynamic items (loaded from the API) are used for groups that require them;
 * static items from NAV_GROUPS are used for everything else.
 */
export function useGroupNavItems(group: NavSidebarGroup): SidebarItem[] {
  const analyticsNavItems = useLiveNav();
  const dashboardsNavItems = useDashboardsNav();
  const reportsNavItems = usePlacementNav("measurement");
  const adminNavItems = useAdminNav();
  const { canAccessSection } = useAuthorization();
  const authorizedGroups = useAuthorizedNavGroups();

  if (group.id === "live") {
    return analyticsNavItems.filter(
      (item) => item.id !== "live-activity" || canAccessSection("journal"),
    );
  }

  if (group.id === "workspace") return dashboardsNavItems;
  if (group.id === "measurement") return reportsNavItems;
  if (group.id === "admin") return adminNavItems;
  if (group.id === "home") {
    return getAuthorizedHomeNavItems((sectionId) =>
      authorizedGroups.some((entry) => entry.id === sectionId),
    );
  }

  return group.items;
}

/**
 * Section-agnostic derivation of the active group / items / item for a given
 * pathname. Shared by both sidebar variants so active-state logic never
 * diverges. Pass `resolvedGroup` to override the route-derived group (e.g. the
 * optimistic space selection) and `useDefaultFallback` to fall back to the
 * group's default item when nothing matches.
 */
export function useNavActiveState(
  pathname: string,
  resolvedGroup?: NavSidebarGroup,
  useDefaultFallback = false,
) {
  const authorizedGroups = useAuthorizedNavGroups();
  const routeActiveGroup = useMemo(
    () => getActiveNavGroup(pathname, authorizedGroups),
    [authorizedGroups, pathname],
  );
  const activeGroup = resolvedGroup ?? routeActiveGroup;
  const activeItems = useGroupNavItems(activeGroup);
  const defaultActiveItemId =
    activeItems.find((item) => item.href === (activeGroup.defaultHref ?? activeGroup.href))?.id ??
    null;
  const activeItemId = getActiveSidebarItemId(
    pathname,
    activeItems,
    useDefaultFallback ? defaultActiveItemId : null,
  );

  return { routeActiveGroup, activeGroup, activeItems, activeItemId, defaultActiveItemId };
}

interface TooltipState {
  label: string;
  midY: number;
  left: number;
}

export function NavSidebar() {
  const location = useLocation();
  const { mode, toggle } = useNavSidebarState();
  const { theme, setTheme } = useTheme();
  const authorizedGroups = useAuthorizedNavGroups();

  const [tip, setTip] = useState<TooltipState | null>(null);
  const [optimisticGroupId, setOptimisticGroupId] = useState<string | null>(null);

  const expanded = mode === "expanded";
  const width = expanded ? NAV_SIDEBAR_W_EXPANDED : NAV_SIDEBAR_W_COLLAPSED;

  const routeActiveGroup = useMemo(
    () => getActiveNavGroup(location.pathname, authorizedGroups),
    [authorizedGroups, location.pathname],
  );

  const optimisticGroup = useMemo(() => {
    if (!optimisticGroupId) return null;

    return authorizedGroups.find((group) => group.id === optimisticGroupId) ?? null;
  }, [authorizedGroups, optimisticGroupId]);

  const activeGroup = optimisticGroup ?? routeActiveGroup;
  const isShowingOptimisticGroup =
    optimisticGroup !== null && optimisticGroup.id !== routeActiveGroup.id;

  const { activeItems, activeItemId } = useNavActiveState(
    location.pathname,
    activeGroup,
    isShowingOptimisticGroup,
  );

  useEffect(() => {
    setOptimisticGroupId(null);
    setTip(null);
  }, [location.pathname]);

  const showTip = (label: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();

    setTip({
      label,
      midY: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  };

  const inactiveNavClass = NAV_ITEM_INACTIVE_CLASS;

  const isDark = theme === "dark";
  const toggleTheme = () => setTheme(isDark ? "light" : "dark");

  const toggleSidebar = () => {
    setTip(null);
    setOptimisticGroupId(null);
    toggle();
  };

  const handleSpaceClick = (group: NavSidebarGroup) => {
    setTip(null);

    if (group.id !== routeActiveGroup.id) {
      setOptimisticGroupId(group.id);
    }
  };

  return (
    <>
      <aside
        className={cn(
          "fixed left-0 top-0 z-20 hidden h-full flex-col overflow-hidden group md:flex",
          "border-r border-border bg-surface",
          "transition-[width] duration-350 ease-[cubic-bezier(0.25,1,0.5,1)] will-change-[width] motion-reduce:transition-none",
        )}
        style={{
          width,
          contain: "layout paint style",
        }}
        aria-label="Main navigation"
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div
          className={cn(
            "flex h-14 max-h-14 shrink-0 items-center overflow-hidden",
            NAV_SECTION_X_PADDING_CLASS,
          )}
        >
          <div
            className={cn("flex w-full items-center", NAV_ITEM_HEIGHT_CLASS, NAV_ITEM_RADIUS_CLASS)}
          >
            <div
              className={cn(
                "relative flex shrink-0 items-center",
                NAV_ITEM_HEIGHT_CLASS,
                expanded ? "w-auto justify-start" : "w-9 justify-center",
              )}
            >
              <Link
                to="/home"
                aria-label="Home"
                onMouseEnter={
                  !expanded ? (event) => showTip("Expand", event.currentTarget) : undefined
                }
                onMouseLeave={!expanded ? () => setTip(null) : undefined}
                onClick={(event) => {
                  if (!expanded) {
                    event.preventDefault();
                    toggleSidebar();
                  }
                }}
                className="flex items-center justify-center"
              >
                <Logo
                  variant={expanded ? "full" : "mark"}
                  size={34}
                  className={cn(
                    "transition-opacity duration-150",
                    !expanded && "group-hover:opacity-0",
                  )}
                />
              </Link>

              {!expanded && (
                <button
                  type="button"
                  onClick={toggleSidebar}
                  aria-label="Expand"
                  className={cn(
                    "absolute inset-0 flex items-center justify-center",
                    NAV_ITEM_RADIUS_CLASS,
                    "text-secondary opacity-0 transition-[color,opacity] duration-150",
                    "group-hover:opacity-100 hover:bg-surface-elevated hover:text-primary",
                  )}
                >
                  <PanelLeftOpen className={cn(NAV_ICON_CLASS, "opacity-75")} />
                </button>
              )}
            </div>

            {expanded && (
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label="Collapse"
                className={cn(
                  "ml-auto flex items-center justify-center",
                  NAV_ITEM_SIZE_CLASS,
                  NAV_ITEM_RADIUS_CLASS,
                  "text-secondary transition-colors hover:bg-surface-elevated hover:text-primary",
                )}
              >
                <PanelLeftClose className={cn(NAV_ICON_CLASS, "opacity-75")} />
              </button>
            )}
          </div>
        </div>

        {/* ── Space Switcher ────────────────────────────────────────── */}
        <div className={cn("shrink-0 border-b border-border py-2", NAV_SECTION_X_PADDING_CLASS)}>
          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                onMouseEnter={
                  !expanded ? (event) => showTip(activeGroup.label, event.currentTarget) : undefined
                }
                onMouseLeave={!expanded ? () => setTip(null) : undefined}
                className={cn(
                  "flex w-full items-center text-sm font-medium transition-colors",
                  NAV_ITEM_HEIGHT_CLASS,
                  NAV_ITEM_RADIUS_CLASS,
                  inactiveNavClass,
                )}
                aria-label={`Current space: ${activeGroup.label}. Switch space`}
              >
                <span
                  className={cn("flex shrink-0 items-center justify-center", NAV_ITEM_SIZE_CLASS)}
                >
                  <LayoutGrid className={cn(NAV_ICON_CLASS, "opacity-75")} />
                </span>

                <span
                  className={cn(
                    "flex min-w-0 flex-1 items-center gap-1 transition-opacity duration-150",
                    expanded ? "opacity-100" : "pointer-events-none opacity-0",
                  )}
                >
                  <span className="flex-1 truncate text-left">{activeGroup.label}</span>
                  <ChevronDown className="mr-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                </span>
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="start"
                side={expanded ? "bottom" : "right"}
                sideOffset={6}
                className={cn(
                  "z-40 min-w-55 rounded-xl border border-border bg-surface p-1.5 shadow-xl",
                  "will-change-[transform,opacity]",
                )}
              >
                <div className="mx-1.5 my-1 h-px bg-border" aria-hidden="true" />

                {authorizedGroups.map((group) => {
                  const GroupIcon = group.icon;
                  const isActive = activeGroup.id === group.id;

                  return (
                    <DropdownMenu.Item key={group.id} asChild>
                      <Link
                        to={getNavGroupEntryHref(group)}
                        onClick={() => handleSpaceClick(group)}
                        className={cn(
                          "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none transition-colors",
                          isActive
                            ? "bg-brand-cyan/10 text-interaction-text"
                            : "text-secondary hover:bg-surface-elevated hover:text-primary",
                        )}
                      >
                        <GroupIcon className="h-4 w-4 shrink-0 opacity-85" />
                        <span className="flex-1 truncate">{group.label}</span>
                        {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
                      </Link>
                    </DropdownMenu.Item>
                  );
                })}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>

        {/* ── Nav items for active space ────────────────────────────── */}
        <SidebarNavItems
          key={activeGroup.id}
          items={activeItems}
          activeItemId={activeItemId}
          expanded={expanded}
          className="relative z-10 flex flex-1 flex-col gap-1 overflow-x-hidden overflow-y-auto px-3 py-3 will-change-[opacity,transform]"
          onItemMouseEnter={(label, el) => showTip(label, el)}
          onItemMouseLeave={() => setTip(null)}
        />

        {/* ── Footer: theme toggle + user ───────────────────────────── */}
        <footer className="relative z-10 mt-auto shrink-0 overflow-hidden py-2">
          {expanded ? (
            <div className="p-2">
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                aria-pressed={isDark}
                className={cn(
                  "flex h-9 w-full items-center rounded-xl transition-colors",
                  inactiveNavClass,
                )}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center">
                  {isDark ? (
                    <Sun className={cn(NAV_ICON_CLASS, "opacity-75")} />
                  ) : (
                    <Moon className={cn(NAV_ICON_CLASS, "opacity-75")} />
                  )}
                </span>

                <span className="min-w-0 flex-1 truncate text-left text-sm">
                  {isDark ? "Light mode" : "Dark mode"}
                </span>
              </button>

              <UserProfile side="top" align="start" />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1 py-2">
              <button
                type="button"
                onClick={toggleTheme}
                onMouseEnter={(event) =>
                  showTip(isDark ? "Light mode" : "Dark mode", event.currentTarget)
                }
                onMouseLeave={() => setTip(null)}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                aria-pressed={isDark}
                className={cn(
                  "flex items-center justify-center",
                  NAV_ITEM_SIZE_CLASS,
                  NAV_ITEM_RADIUS_CLASS,
                  inactiveNavClass,
                )}
              >
                {isDark ? (
                  <Sun className={cn(NAV_ICON_CLASS, "opacity-75")} />
                ) : (
                  <Moon className={cn(NAV_ICON_CLASS, "opacity-75")} />
                )}
              </button>

              <CollapsedUserAvatar
                onTooltip={(label, el) => showTip(label, el)}
                onTooltipHide={() => setTip(null)}
              />
            </div>
          )}
        </footer>
      </aside>

      {tip && (
        <div
          role="tooltip"
          className={cn(
            "pointer-events-none fixed z-50 whitespace-nowrap rounded-lg",
            "border border-border bg-surface px-2.5 py-1.5",
            "text-sm font-medium text-primary shadow-xl",
          )}
          style={{
            left: tip.left,
            top: tip.midY,
            transform: "translateY(-50%)",
          }}
        >
          {tip.label}
        </div>
      )}
    </>
  );
}
